import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Chip,
} from "@material-ui/core";
import { Edit, DeleteOutline, Label as LabelIcon } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../../components/MainContainer";
import MainHeader from "../../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../../components/MainHeaderButtonsWrapper";
import Title from "../../../components/Title";
import ConfirmationModal from "../../../components/ConfirmationModal";
import AgentIaModal from "../../../components/AgentIaModal";
import AgentIaRoutingModal from "../../../components/AgentIaRoutingModal";
import TableRowSkeleton from "../../../components/TableRowSkeleton";

import { listAgents, deleteAgent, updateAgent } from "../../../services/agentIa";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const AgentIaAgents = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [routingAgent, setRoutingAgent] = useState(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAgents();
      setAgents(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleToggleActive = async (agent) => {
    try {
      await updateAgent(agent.id, { active: !agent.active });
      fetchAgents();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAgent(deletingAgent.id);
      toast.success("Agente eliminado.");
      fetchAgents();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingAgent(null);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingAgent && `¿Eliminar el agente "${deletingAgent.name}"?`}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Se borran también sus documentos y su banco de respuestas. No se puede
        deshacer.
      </ConfirmationModal>
      <AgentIaModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingAgent(null);
        }}
        agent={editingAgent}
        onSaved={fetchAgents}
      />
      <AgentIaRoutingModal
        open={!!routingAgent}
        onClose={() => setRoutingAgent(null)}
        agent={routingAgent}
      />
      <MainHeader>
        <Title>Agentes</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setEditingAgent(null);
              setModalOpen(true);
            }}
          >
            Nuevo agente
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nombre</TableCell>
              <TableCell align="center">Etiqueta</TableCell>
              <TableCell align="center">Activo</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.map((agent) => (
              <TableRow key={agent.id}>
                <TableCell align="center">{agent.name}</TableCell>
                <TableCell align="center">
                  {agent.label && <Chip size="small" label={agent.label} />}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={agent.active}
                    onChange={() => handleToggleActive(agent)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    title="Enrutamiento por etiqueta"
                    onClick={() => setRoutingAgent(agent)}
                  >
                    <LabelIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingAgent(agent);
                      setModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeletingAgent(agent);
                      setConfirmOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={4} />}
            {!loading && agents.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Aún no hay agentes. Crea el primero con &ldquo;Nuevo
                  agente&rdquo;.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default AgentIaAgents;
